import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { Col, InputNumber, Row, Switch, TimePicker } from 'antd';
import Form, { FormInstance } from 'antd/lib/form';

import { ClientFeatureConfig } from '../../../types/Feature';
import { QuoteDenyingConfigAttributes } from '../../../../../server/src/models';

const cardHeaderTitleStyle = { fontSize: '18px' };

interface CutOffFormSectionProps {
    isDetailMode: boolean;
    formInstance: FormInstance;
    quoteDenyingConfig?: ClientFeatureConfig<QuoteDenyingConfigAttributes>;
}

const NextDayCutOffExamples = () => (
    <p>
        If you enter the following settings:
        <br />
        - Time of last order: 18:10
        <br />
        - Reopening time: 13:30
        <br />
        any request coming after 18:10 for a delivery the next day before 13:30 will be denied.
        <br />
        A delivery for tomorrow 13:45 requested today at 18:00 would be accepted
        <br />
        A delivery for tomorrow 13:45 requested today at 18:15 would be accepted
        <br />
        A delivery for tomorrow 13:15 requested today at 18:00 would be accepted
        <br />A delivery for tomorrow 13:15 requested today at 18:15 would be denied
    </p>
);

const NextDayCutOffSection = ({
    config,
    isDetailMode,
}: {
    isDetailMode: boolean;
    config?: QuoteDenyingConfigAttributes;
}) => {
    const { timeOfLastOrder, cutOffAfterMidnight, reopeningTime } = config || {};

    return (
        <Row gutter={24}>
            <Col span={2} />
            <Col span={6}>
                <Row gutter={24}>
                    <Col span={24}>
                        <Form.Item
                            name={['nextDayCutOff', 'timeOfLastOrder']}
                            label="Time of last order"
                            tooltip={<>Deliveries for the next day requested after the provided time will be denied</>}
                        >
                            {isDetailMode ? timeOfLastOrder : <TimePicker format="HH:mm" />}
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col span={24}>
                        <Form.Item
                            name={['nextDayCutOff', 'cutOffAfterMidnight']}
                            label="Cutoff is after midnight ?"
                            tooltip={<>If the cutoff for a day happens on that day in the morning</>}
                            valuePropName="checked"
                            className="m-0"
                        >
                            {isDetailMode ? (
                                <>{cutOffAfterMidnight ? 'yes' : 'no'}</>
                            ) : (
                                <Switch
                                    title={
                                        cutOffAfterMidnight ? 'Cutoff is after midnight' : 'Cutoff is before midnight'
                                    }
                                    checkedChildren="yes"
                                    unCheckedChildren="no"
                                />
                            )}
                        </Form.Item>
                    </Col>
                </Row>
            </Col>
            <Col span={6}>
                <Form.Item
                    name={['nextDayCutOff', 'reopeningTime']}
                    label="Reopening time"
                    tooltip="Deliveries for the next day (but after the reopening time) requested after the time of last order"
                >
                    {isDetailMode ? (
                        reopeningTime
                    ) : (
                        <TimePicker format="HH:mm" defaultValue={moment('23:59', 'HH:mm')} />
                    )}
                </Form.Item>
            </Col>
            <Col span={10}>
                <NextDayCutOffExamples />
            </Col>
        </Row>
    );
};

const CutOffFormSection: React.FC<CutOffFormSectionProps> = (props: CutOffFormSectionProps) => {
    const { quoteDenyingConfig, formInstance, isDetailMode } = props;

    useEffect(() => {
        if (quoteDenyingConfig) {
            const tolo = moment(quoteDenyingConfig.config.timeOfLastOrder);
            const rt = moment(quoteDenyingConfig.config.reopeningTime);
            formInstance.setFields([
                {
                    name: ['nextDayCutOff', 'reopeningTime'],
                    value: rt,
                },
                {
                    name: ['nextDayCutOff', 'timeOfLastOrder'],
                    value: tolo,
                },
                {
                    name: ['nextDayCutOff', 'cutOffAfterMidnight'],
                    value: quoteDenyingConfig.config.cutOffAfterMidnight,
                },
                {
                    name: 'sameDayCutoff',
                    value: quoteDenyingConfig.config.sameDayCutoff,
                },
            ]);
        }
    }, [quoteDenyingConfig]);

    return (
        <div className="card border-0" id="next-day-cutoff-section">
            <div className="card-header border-bottom d-flex justify-content-between align-items-center bg-white">
                <p className="m-0 p-0 h5" style={cardHeaderTitleStyle}>
                    Next day delivery cut-off
                </p>
            </div>
            <div className="card-body">
                <NextDayCutOffSection config={quoteDenyingConfig?.config} isDetailMode={isDetailMode} />
            </div>
            <div className="card-header border-bottom d-flex justify-content-between align-items-center bg-white">
                <p className="m-0 p-0 h5" style={cardHeaderTitleStyle}>
                    Same day delivery cut-off (minimum delay before delivery)
                </p>
            </div>
            <div className="card-body">
                <Row gutter={24}>
                    <Col span={2} />
                    <Col span={12}>
                        <Form.Item
                            name="sameDayCutoff"
                            label="Minimum delay before delivery (minutes)"
                            tooltip="Delivery requests with a shorter delay will be denied"
                        >
                            {isDetailMode ? quoteDenyingConfig?.config?.sameDayCutoff : <InputNumber />}
                        </Form.Item>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default CutOffFormSection;
